import React, {useCallback, useContext} from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';

import PersonIcon from '@mui/icons-material/Person';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ConstructionIcon from '@mui/icons-material/Construction';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';

import {SEARCH_TYPE_ORDER} from '@embertracking/common/data/search/types.mjs';

import {UserContext} from '-/contexts/User';
import {hasPermission} from '-/data/utils';

// Icons per result type. Deliberately the SAME glyphs useMenuItems.js gives the
// nav (FireTruck for apparatus, Whatshot for incidents, FactCheck for training,
// People/Person for members) — a result should look like the place it lives.
const TYPE_ICONS = {
    members: PersonIcon,
    incidents: WhatshotIcon,
    training: FactCheckIcon,
    apparatus: FireTruckIcon,
    requirements: LocalLibraryIcon,
    assignments: AssignmentTurnedInIcon,
    gear: ConstructionIcon,
    fuel: LocalGasStationIcon,
    preplans: ApartmentIcon,
    publicEducation: GroupsIcon,
    reports: AssessmentIcon,
    settings: SettingsIcon,
    pages: ArrowForwardIcon,
    help: HelpOutlinedIcon
};

export const searchTypeIcon = type => TYPE_ICONS[type] || SearchIcon;

export const SearchTypeIcon = ({type, ...props}) => {
    const Icon = searchTypeIcon(type);
    return <Icon {...props} />;
};

export {SEARCH_TYPE_ORDER};

// The highlight the design draws on a match (--et-warning-cream). Rendered as a
// real <mark> so it survives a browser find-in-page and reads correctly to a
// screen reader, with the browser's default yellow overridden.
const MARK_SX = {
    backgroundColor: '#ffedce',
    color: 'text.primary',
    borderRadius: '2px',
    px: '2px',
    py: 0
};

/**
 * Render a {pre, hit, post} split from the matcher. The server computes the
 * offsets from the same tokens it ranked with, so the mark always sits on what
 * actually matched — the client never re-guesses.
 */
export const Highlight = ({parts, sx}) => {
    if (!parts) return null;
    const {pre = '', hit = '', post = ''} = parts;
    return (
        <Box component="span" sx={sx}>
            {pre}
            {hit ? <Box component="mark" sx={MARK_SX}>{hit}</Box> : null}
            {post}
        </Box>
    );
};

/**
 * The line under a result's title: what QUALIFIES this record, never the title
 * again.
 *
 * Repeating the title here (as the mock did) is fine when every record has a
 * unique name and actively harmful when they don't — a department runs "Relay
 * Pumping" every few months, so three real, different sessions rendered as three
 * identical rows with no way to tell them apart. What distinguishes them is the
 * subtitle and the date, so those are what the row shows.
 *
 * `withType` prefixes the type ("Training records · …") for contexts with no
 * group heading above the row — the Recently viewed list.
 */
export const resultPath = (result, typeLabel, {withType = false} = {}) => {
    const parts = [withType ? typeLabel : null, result.subtitle].filter(Boolean);
    // Nothing to qualify it with (a nav destination, an untyped record) — fall
    // back to the type so the line is never empty.
    return parts.length ? parts.join(' · ') : typeLabel;
};

/** Short, unambiguous date for a result row — the tiebreak between same-titled records. */
export const resultDate = result => (result.date ? moment(result.date).format('MMM D, YYYY') : '');

/** A result's URL, deep-linked to the tab whose field matched where there is one. */
export const resultHref = result => {
    const tab = result.snippet?.tab;
    return tab ? `${result.url}/${tab}` : result.url;
};

/**
 * Is this click one the BROWSER should handle rather than the router?
 *
 * Search rows are real anchors, so ⌘/Ctrl-click (new tab), Shift-click (new
 * window), Alt-click (save link) and middle-click all have a native meaning.
 * Calling preventDefault() on those — which a row must do for a PLAIN click, so
 * navigation stays client-side — silently eats the gesture and routes in place
 * instead, which is what made "open this result in a new tab" impossible.
 *
 * `button === 1` covers middle-click: it arrives as mousedown/auxclick rather
 * than a modified click, and autoscroll would otherwise start on the row.
 */
export const isModifiedClick = event =>
    Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1);

// Finding a record and OPENING it are two different permissions, and the detail
// routes in Router.js are gated more tightly than the read permissions /search
// filters on: a member with training.read can legitimately find a session (the
// title, date and snippet answer "does this exist and what did it say?") but
// `/training/:id` only exists for training.write. The grids already encode this
// — trainings/Grid.js and incidents/Grid.js both make rows non-clickable without
// the write permission — so search matches them rather than routing someone into
// a 404.
//
// Keep in lockstep with the route gates in Router.js.
const OPEN_PERMISSION = {
    training: 'training.write',
    incidents: 'incidents.write',
    apparatus: 'apparatus.read',
    members: 'users.write',
    requirements: 'requirements.read',
    gear: 'gear.read',
    fuel: 'fuel.read',
    preplans: 'preplans.read',
    publicEducation: 'publicEducation.read'
};

/**
 * Can this member open the record behind a result? Nav destinations ("Go to")
 * are always openable — they come from useMenuItems(), which only lists pages
 * the member can already reach.
 */
export const useCanOpenResult = () => {
    const {currentUser} = useContext(UserContext);
    return useCallback(result => {
        const permission = OPEN_PERMISSION[result?.type];
        if (!permission) return true;
        // Boolean(), not the raw claim: hasPermission returns `undefined` for a
        // permission the member doesn't hold, and the rows take an `openable` /
        // `enabled` prop that DEFAULTS TO TRUE — so an undefined answer read as
        // "openable" and drew the row (and now its href) as if it were.
        return Boolean(hasPermission(currentUser, permission));
    }, [currentUser]);
};
